/**
 * Brand scope of a coupon (KAN-227). `applicableBrands` holds canonical `brands/{brandId}` document ids, or exactly `['all']`
 * when the administrator deliberately chose "All brands". A missing or empty scope is never valid for issuing a coupon.
 */
export const ALL_BRANDS_ID = 'all';
export const ALL_BRANDS_LABEL = 'All brands';
export const BRAND_NOT_SET_LABEL = 'Brand not set';
/** Sender / signature used when a coupon is valid for several brands or for all brands. */
export const MULTI_BRAND_ISSUER_NAME = 'KCEG';

export interface BrandRef {
  id?: string;
  name: string;
}

/** Trims, drops empty values and removes duplicates, keeping order. */
export function normalizeBrandScope(ids: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of ids || []) {
    const id = String(raw ?? '').trim();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

export function isAllBrandsScope(ids: readonly string[] | null | undefined): boolean {
  const scope = normalizeBrandScope(ids);
  return scope.length === 1 && scope[0] === ALL_BRANDS_ID;
}

/**
 * Returns an error message when the scope cannot be used to issue a coupon, otherwise `null`.
 * Rules: at least one brand, `all` only on its own, and (when a brand list is given) every id must be a known brand.
 */
export function validateBrandScope(ids: readonly string[] | null | undefined, knownBrands?: readonly BrandRef[]): string | null {
  const scope = normalizeBrandScope(ids);
  if (scope.length === 0) {
    return 'Select at least one brand, or choose "All brands" deliberately. A coupon cannot be issued without a brand scope.';
  }
  if (scope.includes(ALL_BRANDS_ID) && scope.length > 1) {
    return '"All brands" cannot be combined with specific brands. Choose either All brands or specific brands.';
  }
  if (knownBrands && !isAllBrandsScope(scope)) {
    const knownIds = new Set(knownBrands.map(b => b.id).filter((id): id is string => !!id));
    const unknown = scope.filter(id => !knownIds.has(id));
    if (unknown.length > 0) {
      return `Unknown brand selected (${unknown.join(', ')}). Pick brands from the list.`;
    }
  }
  return null;
}

/** Brand names of a scope, resolved from the canonical brands list (never hardcoded). Unknown ids stay visible. */
export function brandScopeNames(ids: readonly string[] | null | undefined, brands: readonly BrandRef[]): string[] {
  const scope = normalizeBrandScope(ids);
  if (scope.length === 0 || isAllBrandsScope(scope)) return [];
  return scope.map(id => {
    const brand = brands.find(b => b.id === id);
    return brand?.name?.trim() || `Unknown brand (${id})`;
  });
}

/** Text shown on the table, details, voucher, ZIP card and email: "All brands", brand names, or "Brand not set" for legacy coupons. */
export function brandScopeLabel(ids: readonly string[] | null | undefined, brands: readonly BrandRef[]): string {
  const scope = normalizeBrandScope(ids);
  if (scope.length === 0) return BRAND_NOT_SET_LABEL;
  if (isAllBrandsScope(scope)) return ALL_BRANDS_LABEL;
  return brandScopeNames(scope, brands).join(', ');
}

/** Name used as email sender / signature: the brand for a single-brand coupon, otherwise the multi-brand issuer name. */
export function brandIssuerName(ids: readonly string[] | null | undefined, brands: readonly BrandRef[]): string {
  const names = brandScopeNames(ids, brands);
  return names.length === 1 ? names[0] : MULTI_BRAND_ISSUER_NAME;
}

/**
 * Automatically resolves the PhotoLaVie brand from a list of brands (case/punctuation-insensitive).
 * Supports canonical "K-Photo LaVie" and alternate names like "Photo LaVie", "LaVie".
 */
export function findPhotoLavieBrand<T extends { id?: string; name?: string; isDeleted?: boolean }>(
  brands: readonly T[] | null | undefined
): { brandId: string; brandName: string } | null {
  const activeBrands = (brands || []).filter(b => b && b.isDeleted !== true && typeof b.name === 'string');
  const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  // 1. Exact normalized match for "kphotolavie" (e.g. "K-Photo LaVie", "k-photo lavie", "K Photo Lavie")
  let match = activeBrands.find(b => norm(b.name!) === 'kphotolavie');
  if (match && match.id) {
    return { brandId: match.id, brandName: match.name || 'K-Photo LaVie' };
  }

  // 2. Alternate match for "photolavie" (e.g. "Photo LaVie", "photo lavie", "PhotoLavie")
  match = activeBrands.find(b => norm(b.name!) === 'photolavie');
  if (match && match.id) {
    return { brandId: match.id, brandName: match.name || 'K-Photo LaVie' };
  }

  // 3. Alternate match for "lavie" (e.g. "LaVie", "lavie")
  match = activeBrands.find(b => norm(b.name!) === 'lavie');
  if (match && match.id) {
    return { brandId: match.id, brandName: match.name || 'K-Photo LaVie' };
  }

  // 4. Substring normalized contains "lavie"
  match = activeBrands.find(b => norm(b.name!).includes('lavie'));
  if (match && match.id) {
    return { brandId: match.id, brandName: match.name || 'K-Photo LaVie' };
  }

  // 5. Case-insensitive regex fallback
  const regex = /k-?photo\s*lavie|photo\s*lavie|lavie/i;
  match = activeBrands.find(b => regex.test(b.name!));
  if (match && match.id) {
    return { brandId: match.id, brandName: match.name || 'K-Photo LaVie' };
  }

  return null;
}


/** Placeholder meaning "no radio chosen yet" in the single-choice picker. */
export const NO_BRAND_SELECTED = '';

/** True for the retired Life4Cuts brand, matched by normalized name ("LFC", "Life4Cuts", "LFC - Life4Cuts"). */
export function isExcludedCouponBrand(brand: { name?: string } | null | undefined): boolean {
  const norm = (brand?.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm.includes('lfc') || norm.includes('life4cuts');
}

/** Brands an administrator may pick when issuing a coupon (KAN-363): active, not LFC, alphabetical. */
export function eligibleCouponBrands<T extends { name?: string; isDeleted?: boolean }>(brands: readonly T[] | null | undefined): T[] {
  return (brands || [])
    .filter(b => b && b.isDeleted !== true && !!b.name && !isExcludedCouponBrand(b))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
}

/**
 * Stricter scope rule for issuing or re-scoping a coupon in the ERP (KAN-363): exactly one brand id or exactly `['all']`,
 * never the excluded LFC brand. Legacy multi-brand / LFC coupons stay readable; they just cannot be saved again as such.
 * Returns an error message, or `null` when valid.
 */
export function validateIssuanceBrandScope(ids: readonly string[] | null | undefined, knownBrands: readonly BrandRef[]): string | null {
  const scope = normalizeBrandScope(ids);
  if (scope.length === 0) return 'Select a brand or All brands to continue.';
  if (scope.length > 1) return 'Choose exactly one brand, or All brands. Multiple brands are no longer supported.';
  if (isAllBrandsScope(scope)) return null;
  const brand = knownBrands.find(b => b.id === scope[0]);
  if (!brand) return `Unknown brand selected (${scope[0]}). Pick a brand from the list.`;
  if (isExcludedCouponBrand(brand)) return 'This brand is no longer available for coupons. Pick another brand or All brands.';
  return null;
}

/** Notice for the edit modal when a stored scope can no longer be saved as-is; `null` when it can be preselected. */
export function legacyBrandScopeNotice(ids: readonly string[] | null | undefined, knownBrands: readonly BrandRef[]): string | null {
  const scope = normalizeBrandScope(ids);
  if (scope.length === 0 || isAllBrandsScope(scope)) return null;
  const multi = scope.length > 1;
  const lfc = scope.some(id => isExcludedCouponBrand(knownBrands.find(b => b.id === id)));
  const unknown = scope.some(id => !knownBrands.some(b => b.id === id));
  if (!multi && !lfc && !unknown) return null;
  if (multi) {
    return 'This coupon was set up for multiple brands, which is no longer supported. Pick one brand or All brands before saving.'
      + (lfc ? ' Life4Cuts is no longer available.' : '');
  }
  if (lfc) return 'This coupon was linked to Life4Cuts, which is no longer available. Pick a brand or All brands before saving.';
  return 'This coupon\'s brand no longer exists. Pick a brand or All brands before saving.';
}

/** The single selectable value for a stored scope: the brand id or `all`, or nothing when it is legacy/invalid. */
export function selectableBrandValue(ids: readonly string[] | null | undefined, knownBrands: readonly BrandRef[]): string {
  const scope = normalizeBrandScope(ids);
  return validateIssuanceBrandScope(scope, knownBrands) === null ? scope[0] : NO_BRAND_SELECTED;
}
